import { useQuery } from 'data-fetching-client';
import { GET_PRODUCT_BADGES_QUERY } from '../../../../__generated__/chirp/com/hubspot/monetization/rpc/BadgeInAppService.dfc.new';
import { GET_PRODUCT_BADGES_QUERY as GET_PRODUCT_BADGES_QUERY_PUBLIC } from '../../../../__generated__/chirp/com/hubspot/monetization/rpc/BadgePublicService.dfc.new';
import { isValidApiName } from '../../utils/typeguards';
import { useIsInAppAuth } from '../user/useUserInfo';
const queryToUse = isInAppAuth => {
  return isInAppAuth ? GET_PRODUCT_BADGES_QUERY : GET_PRODUCT_BADGES_QUERY_PUBLIC;
};

/**
 * Manages badges for multiple products displayed together (ie merch cards).
 * Handles badge service integration, FTS experiment, upsell starter card, and trial detection for positioning.
 *
 * @param productApiNames - Array of product API names to fetch badges for
 * @returns Badge data and grid layout helpers
 * @returns badges - Product badges keyed by API name from badge service
 * @returns hasBadgeOnFirstColumn - Whether first column has any badge (service/trial/experiment)
 * @returns hasBadgeOnLastColumn - Whether last column has any badge (service/trial)
 * @returns loading - Badge service loading state
 * @returns error - Badge service error state
 */
export const useBadges = ({
  productApiNames,
  skipOverride = false
}) => {
  const isInAppAuth = useIsInAppAuth();
  const query = queryToUse(isInAppAuth);
  const validProductApiNames = productApiNames.filter(productApiName => isValidApiName(productApiName));
  const {
    data,
    loading,
    error
  } = useQuery(query, {
    variables: {
      productApiNames: validProductApiNames
    },
    skip: validProductApiNames.length === 0 || skipOverride
  });
  const productBadges = data === null || data === void 0 ? void 0 : data.result.productBadges;
  const firstColumnApiName = validProductApiNames[0];
  const lastColumnApiName = validProductApiNames[validProductApiNames.length - 1];
  if (skipOverride) {
    return {
      badges: undefined,
      hasBadgeOnFirstColumn: false,
      hasBadgeOnLastColumn: false,
      loading: false,
      error: null
    };
  }
  const hasBadgeOnFirstColumn = !!(productBadges !== null && productBadges !== void 0 && productBadges[firstColumnApiName]);
  const hasBadgeOnLastColumn = !!(productBadges !== null && productBadges !== void 0 && productBadges[lastColumnApiName]);
  return {
    badges: productBadges,
    hasBadgeOnFirstColumn,
    hasBadgeOnLastColumn,
    loading,
    error
  };
};